import { useLiveQuery } from 'dexie-react-hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { chargeReelle, repartitionDe } from '../../analysis/donnees'
import { calculerRepartition, etatCreance } from '../../analysis/repartition'
import { ajouterPiece, lirePiece, listerPieces, supprimerPiece, type MetaPiece } from '../../db/pieces'
import {
  enregistrerModele,
  enregistrerOperation,
  enregistrerReglement,
  supprimerOperation,
  supprimerReglement,
} from '../../db/repo'
import { aujourdhui, formatJour } from '../../domain/temps'
import type { Cents, Id, Sens } from '../../domain/types'
import {
  Bascule,
  Carte,
  Champ,
  ChampMontant,
  Confirmation,
  Encart,
  Entete,
  IconePlus,
  IconePoubelle,
  Montant,
  Puce,
  SelectCategorie,
  SelectCompte,
  useAction,
} from '../composants'
import { useDonnees } from '../donnees'
import { ApercuCascade, EditeurParts, cle, nouvellePart, versCalculables, type PartEdition } from '../EditeurParts'

export function PageOperation() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const { ix } = useDonnees()

  if (id && !ix.operationParId.get(id)) {
    // Juste apres une creation, la requete reactive n'a pas encore relu la
    // base : on attend plutot que de monter un formulaire vide, qui ne se
    // re-remplirait pas quand l'operation arriverait.
    return (
      <>
        <Entete titre="Opération" retour />
        <main className="contenu">
          <p className="vide">{params.get('cree') ? 'Chargement…' : 'Cette opération n’existe plus.'}</p>
        </main>
      </>
    )
  }
  return <FormulaireOperation key={id ?? 'nouvelle'} />
}

function FormulaireOperation() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const { d, ix, reglages } = useDonnees()
  const navigate = useNavigate()
  const existante = id ? ix.operationParId.get(id) : undefined
  const nouvelle = !id

  const [sens, setSens] = useState<Sens>(existante?.sens ?? (params.get('sens') === 'revenu' ? 'revenu' : 'depense'))
  const [montant, setMontant] = useState<Cents | null>(existante?.montant ?? null)
  const [date, setDate] = useState(existante?.date ?? params.get('date') ?? aujourdhui())
  const [libelle, setLibelle] = useState(existante?.libelle ?? '')
  const [compteId, setCompteId] = useState<Id | null>(
    existante?.compteId ?? params.get('compte') ?? reglages.compteParDefautId ?? d.comptes.find((c) => !c.archive)?.id ?? null,
  )
  const [categorieId, setCategorieId] = useState<Id | null>(existante?.categorieId ?? null)
  const [note, setNote] = useState(existante?.note ?? '')
  const [pointee, setPointee] = useState<0 | 1>(existante?.pointee ?? 0)
  const [parts, setParts] = useState<PartEdition[]>(() =>
    existante ? (ix.partsParOperation.get(existante.id) ?? []).sort((a, b) => a.ordre - b.ordre).map((p) => ({ ...p, cle: cle() })) : [],
  )
  // Tant que l'utilisateur n'a pas touche a la cascade, changer de categorie
  // peut la pre-remplir avec le modele par defaut. Des qu'il l'a modifiee, on
  // ne l'ecrase plus.
  const partsTouchees = useRef(!nouvelle)

  const [confirmerSuppression, setConfirmerSuppression] = useState(false)
  const [nomModele, setNomModele] = useState<string | null>(null)
  const { erreur, enCours, executer } = useAction()

  useEffect(() => {
    if (partsTouchees.current || sens !== 'depense' || !categorieId) return
    const cat = ix.categorieParId.get(categorieId)
    const parent = cat?.parentId ? ix.categorieParId.get(cat.parentId) : undefined
    const modeleId = cat?.modeleParDefautId ?? parent?.modeleParDefautId ?? null
    const modele = modeleId ? ix.modeleParId.get(modeleId) : undefined
    setParts(modele ? modele.parts.map((p) => ({ ...p, cle: cle() })) : [])
  }, [categorieId, sens, ix])

  const toucher = (f: (p: PartEdition[]) => PartEdition[]) => {
    partsTouchees.current = true
    setParts((p) => f(p))
  }

  const apercu = useMemo(() => calculerRepartition(montant ?? 0, versCalculables(parts), d.moiId), [montant, parts, d.moiId])

  const enregistrer = () =>
    executer(async () => {
      if (!montant || montant <= 0) throw new Error('Indiquez un montant.')
      if (!compteId) throw new Error('Choisissez un compte.')
      if (!categorieId) throw new Error('Choisissez une catégorie.')
      if (sens === 'depense' && parts.some((p) => !p.tiersId)) throw new Error('Chaque part doit désigner un tiers.')
      const nouvelId = await enregistrerOperation(
        {
          id: existante?.id,
          creeLe: existante?.creeLe,
          sens,
          montant,
          date,
          libelle: libelle.trim(),
          compteId,
          categorieId,
          note,
          pointee,
          recurrenceId: existante?.recurrenceId ?? null,
          occurrence: existante?.occurrence ?? null,
        },
        sens === 'depense' ? parts.map(({ cle: _c, ...p }, i) => ({ ...p, ordre: i + 1 })) : [],
      )
      // Apres une creation, on reste sur l'operation : c'est le moment de
      // photographier la facture ou d'enregistrer un premier remboursement.
      if (nouvelle) navigate(`/operations/${nouvelId}?cree=1`, { replace: true })
      else navigate(-1)
    })

  const tiersAutres = d.tiers.filter((t) => t.type !== 'moi' && !t.archive)

  return (
    <>
      <Entete titre={nouvelle ? 'Nouvelle opération' : 'Opération'} retour />
      <main className="contenu">
        {params.get('cree') && <Encart ton="ok">Opération enregistrée. Vous pouvez maintenant joindre la facture ou saisir un remboursement.</Encart>}

        <Carte>
          <div className="pile">
            <Bascule
              etiquette="Sens"
              valeur={sens}
              onChange={setSens}
              options={[
                { valeur: 'depense', libelle: 'Dépense' },
                { valeur: 'revenu', libelle: 'Revenu' },
              ]}
            />
            <Champ libelle="Montant (CHF)">
              <ChampMontant valeur={montant} onChange={setMontant} grand autoFocus={nouvelle} />
            </Champ>
            <Champ libelle="Libellé">
              <input type="text" value={libelle} placeholder="Dentiste, Migros, loyer…" onChange={(e) => setLibelle(e.target.value)} />
            </Champ>
            <div className="grille-2">
              <Champ libelle="Date">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </Champ>
              <Champ libelle="Compte">
                <SelectCompte comptes={d.comptes} valeur={compteId} onChange={setCompteId} />
              </Champ>
            </div>
            <Champ libelle="Catégorie">
              <SelectCategorie categories={d.categories} sens={sens} valeur={categorieId} onChange={setCategorieId} />
            </Champ>
            <Champ libelle="Note">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
            </Champ>
            <label className="case">
              <input type="checkbox" checked={pointee === 1} onChange={(e) => setPointee(e.target.checked ? 1 : 0)} />
              Pointée sur le relevé
            </label>
            {existante?.recurrenceId && (
              <p className="tres-doux">
                Échéance du {formatJour(existante.occurrence ?? existante.date)} de la récurrence{' '}
                <Link to={`/recurrences/${existante.recurrenceId}`}>{ix.d.recurrences.find((r) => r.id === existante.recurrenceId)?.libelle}</Link>.
              </p>
            )}
          </div>
        </Carte>

        {sens === 'depense' && (
          <Carte
            titre="Répartition"
            action={
              d.modeles.length > 0 && (
                <select
                  aria-label="Appliquer un modèle"
                  value=""
                  style={{ width: 'auto', minHeight: 36, padding: '4px 8px' }}
                  onChange={(e) => {
                    const m = ix.modeleParId.get(e.target.value)
                    if (m) toucher(() => m.parts.map((p) => ({ ...p, cle: cle() })))
                  }}
                >
                  <option value="">Modèle…</option>
                  {d.modeles.map((m) => (
                    <option key={m.id} value={m.id}>{m.nom}</option>
                  ))}
                </select>
              )
            }
          >
            <div className="pile">
              {parts.length === 0 && (
                <p className="doux">
                  Entièrement à votre charge. Ajoutez des parts si une assurance rembourse ou si une autre personne doit participer.
                </p>
              )}
              <EditeurParts parts={parts} onChange={toucher} />
              <div className="boutons">
                <button type="button" className="btn btn--petit" onClick={() => toucher((l) => [...l, nouvellePart(tiersAutres[0]?.id ?? '', l.length + 1)])}>
                  <IconePlus /> Ajouter une part
                </button>
                {parts.length > 0 && nomModele === null && (
                  <button type="button" className="btn btn--fantome btn--petit" onClick={() => setNomModele('')}>
                    Enregistrer comme modèle
                  </button>
                )}
              </div>
              {tiersAutres.length === 0 && (
                <p className="tres-doux">
                  Aucun tiers : créez d’abord la caisse maladie ou la personne concernée dans <Link to="/tiers/nouveau">Tiers</Link>.
                </p>
              )}

              {nomModele !== null && (
                <div className="ligne">
                  <input type="text" className="grandir" placeholder="Nom du modèle" value={nomModele} autoFocus onChange={(e) => setNomModele(e.target.value)} />
                  <button
                    type="button"
                    className="btn btn--petit btn--principal"
                    disabled={!nomModele.trim()}
                    onClick={() =>
                      void executer(async () => {
                        await enregistrerModele({ nom: nomModele.trim(), parts: parts.map(({ cle: _c, id: _i, ...p }, i) => ({ ...p, ordre: i + 1 })) })
                        setNomModele(null)
                      })
                    }
                  >
                    OK
                  </button>
                </div>
              )}

              {parts.length > 0 && montant !== null && montant > 0 && <ApercuCascade apercu={apercu} />}
            </div>
          </Carte>
        )}

        {erreur && <Encart ton="danger">{erreur}</Encart>}

        <button type="button" className="btn btn--principal btn--bloc" disabled={enCours} onClick={() => void enregistrer()}>
          Enregistrer
        </button>

        {existante && existante.sens === 'depense' && <Remboursements operationId={existante.id} />}
        {existante && <PiecesJointes operationId={existante.id} />}

        {existante && (
          <button type="button" className="btn btn--danger btn--bloc" onClick={() => setConfirmerSuppression(true)}>
            Supprimer l’opération
          </button>
        )}
      </main>

      {confirmerSuppression && existante && (
        <Confirmation
          titre="Supprimer l’opération ?"
          message="Ses parts, les remboursements déjà enregistrés et les pièces jointes seront supprimés avec elle."
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmerSuppression(false)}
          onConfirmer={() => {
            void supprimerOperation(existante.id).then(() => navigate(-1))
          }}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Remboursements
// ---------------------------------------------------------------------------

function Remboursements({ operationId }: { operationId: Id }) {
  const { d, ix } = useDonnees()
  const op = ix.operationParId.get(operationId)!
  const { erreur, executer } = useAction()
  const [saisie, setSaisie] = useState<{ partId: Id; montant: Cents | null; date: string; compteId: Id | null } | null>(null)

  // Lecture des parts **enregistrees** : un reglement se rattache a une part
  // existante, jamais a une ligne en cours d'edition.
  const lignes = repartitionDe(ix, op).lignes.filter((l) => l.part.tiersId !== d.moiId)
  if (lignes.length === 0) return null

  return (
    <Carte titre="Remboursements">
      <p className="tres-doux" style={{ marginBottom: 10 }}>
        À ma charge : <Montant valeur={chargeReelle(ix, op)} /> sur <Montant valeur={op.montant} />
      </p>
      <ul className="liste">
        {lignes.map((l) => {
          const c = etatCreance(l.part, l.montant, ix.reglementsParPart.get(l.part.id) ?? [])
          return (
            <li key={l.part.id} className="part">
              <div className="ligne ligne--espacee">
                <span className="gras">{ix.tiersParId.get(l.part.tiersId)?.nom}</span>
                <Puce ton={c.statut === 'soldee' ? 'ok' : c.statut === 'partielle' ? 'info' : 'alerte'}>
                  {c.statut === 'soldee' ? 'soldé' : c.statut === 'partielle' ? 'partiel' : 'attendu'}
                </Puce>
              </div>
              <div className="doux mono">
                dû <Montant valeur={c.du} /> · reçu <Montant valeur={c.recu} /> · reste <Montant valeur={c.restant} />
              </div>
              {c.reglements.map((reg) => (
                <div key={reg.id} className="ligne ligne--espacee tres-doux">
                  <span>{formatJour(reg.date)} · {ix.compteParId.get(reg.compteId)?.nom}</span>
                  <span className="ligne">
                    <Montant valeur={reg.montant} />
                    <button type="button" className="btn btn--fantome btn--petit" aria-label="Supprimer ce remboursement" onClick={() => void supprimerReglement(reg.id)}>
                      <IconePoubelle />
                    </button>
                  </span>
                </div>
              ))}
              {saisie?.partId === l.part.id ? (
                <div className="pile">
                  <div className="grille-2">
                    <Champ libelle="Montant reçu">
                      <ChampMontant valeur={saisie.montant} onChange={(m) => setSaisie({ ...saisie, montant: m })} />
                    </Champ>
                    <Champ libelle="Date">
                      <input type="date" value={saisie.date} onChange={(e) => setSaisie({ ...saisie, date: e.target.value })} />
                    </Champ>
                  </div>
                  <Champ libelle="Crédité sur">
                    <SelectCompte comptes={d.comptes} valeur={saisie.compteId} onChange={(v) => setSaisie({ ...saisie, compteId: v })} />
                  </Champ>
                  <div className="boutons">
                    <button type="button" className="btn btn--petit" onClick={() => setSaisie(null)}>Annuler</button>
                    <button
                      type="button"
                      className="btn btn--petit btn--principal"
                      onClick={() =>
                        void executer(async () => {
                          if (!saisie.montant || !saisie.compteId) throw new Error('Montant et compte requis.')
                          await enregistrerReglement({ partId: saisie.partId, montant: saisie.montant, date: saisie.date, compteId: saisie.compteId, note: '' })
                          setSaisie(null)
                        })
                      }
                    >
                      Enregistrer
                    </button>
                  </div>
                </div>
              ) : (
                c.restant > 0 && (
                  <button
                    type="button"
                    className="btn btn--petit"
                    onClick={() => setSaisie({ partId: l.part.id, montant: c.restant, date: aujourdhui(), compteId: op.compteId })}
                  >
                    Encaisser
                  </button>
                )
              )}
            </li>
          )
        })}
      </ul>
      {erreur && <Encart ton="danger">{erreur}</Encart>}
    </Carte>
  )
}

// ---------------------------------------------------------------------------
// Pieces jointes
// ---------------------------------------------------------------------------

function PiecesJointes({ operationId }: { operationId: Id }) {
  const pieces = useLiveQuery(() => listerPieces(operationId), [operationId]) ?? []
  const { erreur, enCours, executer } = useAction()
  const entree = useRef<HTMLInputElement>(null)

  return (
    <Carte titre="Factures et justificatifs">
      <div className="vignettes">
        {pieces.map((p) => (
          <Vignette key={p.id} piece={p} />
        ))}
        <button type="button" className="vignette" disabled={enCours} onClick={() => entree.current?.click()}>
          {enCours ? 'Ajout…' : '+ Photo ou PDF'}
        </button>
      </div>
      <input
        ref={entree}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={(e) => {
          const fichiers = [...(e.target.files ?? [])]
          e.target.value = ''
          void executer(async () => {
            for (const f of fichiers) await ajouterPiece(operationId, f)
          })
        }}
      />
      {erreur && <Encart ton="danger">{erreur}</Encart>}
    </Carte>
  )
}

function Vignette({ piece }: { piece: MetaPiece }) {
  const [url, setUrl] = useState<string | null>(null)
  const [confirmer, setConfirmer] = useState(false)

  useEffect(() => {
    let lien: string | null = null
    let vivant = true
    void lirePiece(piece.id).then((p) => {
      if (!p || !vivant) return
      lien = URL.createObjectURL(p.blob)
      setUrl(lien)
    })
    return () => {
      vivant = false
      if (lien) URL.revokeObjectURL(lien)
    }
  }, [piece.id])

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="vignette" style={{ width: '100%' }} onClick={() => url && window.open(url, '_blank')} aria-label={`Ouvrir ${piece.nom}`}>
        {piece.type === 'application/pdf' ? <span>PDF<br />{piece.nom}</span> : url ? <img src={url} alt={piece.nom} /> : '…'}
      </button>
      <button
        type="button"
        className="btn btn--petit"
        style={{ position: 'absolute', top: 4, right: 4, minHeight: 28, padding: '2px 6px' }}
        aria-label="Supprimer la pièce"
        onClick={() => setConfirmer(true)}
      >
        <IconePoubelle />
      </button>
      {confirmer && (
        <Confirmation
          titre="Supprimer la pièce jointe ?"
          message={piece.nom}
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmer(false)}
          onConfirmer={() => void supprimerPiece(piece.id)}
        />
      )}
    </div>
  )
}
